import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import ApartmentIcon from '@mui/icons-material/Apartment'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import organizationSessionStore from '../../stores/organizationSessionStore'
import organizationManagementStore from '../../stores/organizationManagementStore'
import i18nStore from '../../stores/i18nStore'
import {
	canCreateTeam,
	isOrganizationSelected,
	organizationLabel,
} from '../../tenancy/organizationUiPolicy'
import { invitationStatusLabel, membershipRoleLabel } from '../../tenancy/organizationPresentation'
import sessionStore from '../../stores/sessionStore'

function translation(key) {
	if (!key) return null
	return key.split('.').reduce((value, part) => value?.[part], i18nStore.translations) || null
}

const OrganizationSwitcher = observer(() => {
	const [anchor, setAnchor] = useState(null)
	const [dialog, setDialog] = useState(null)
	const [teamName, setTeamName] = useState('')
	const [email, setEmail] = useState('')
	const [role, setRole] = useState('MEMBER')
	const [token, setToken] = useState('')
	const selected = organizationSessionStore.selectedOrganization
	const label = organizationLabel(selected, translation)
	const roleOptions = organizationManagementStore.invitationRoles

	function closeDialog() {
		setDialog(null)
		organizationManagementStore.clearError()
	}

	async function chooseOrganization(organizationId) {
		setAnchor(null)
		if (organizationId === organizationSessionStore.selectedOrganizationId) return
		await organizationManagementStore.switchOrganization(organizationId)
	}

	async function submitTeam(event) {
		event.preventDefault()
		const result = await organizationManagementStore.createTeam(teamName)
		if (result) {
			setTeamName('')
			closeDialog()
		}
	}

	async function submitInvitation(event) {
		event.preventDefault()
		const result = await organizationManagementStore.createInvitation(email, role)
		if (result) setEmail('')
	}

	async function submitAcceptance(event) {
		event.preventDefault()
		const result = await organizationManagementStore.acceptInvitation(token)
		if (result) {
			setToken('')
			closeDialog()
		}
	}

	return (
		<>
			{organizationManagementStore.incomingInvitations.length > 0 && (
				<Button color='inherit' onClick={() => setDialog('accept')}>
					Приглашения ({organizationManagementStore.incomingInvitations.length})
				</Button>
			)}
			<Button
				color='inherit'
				startIcon={<ApartmentIcon />}
				onClick={event => setAnchor(event.currentTarget)}
				aria-label={`Текущая организация: ${label}`}
				aria-haspopup='menu'
				aria-expanded={Boolean(anchor)}
				disabled={!organizationSessionStore.isReady}
				sx={{ maxWidth: { xs: 180, sm: 280 }, textTransform: 'none' }}
			>
				<Box component='span' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{label}
				</Box>
				{organizationSessionStore.membershipRole && (
					<Chip label={membershipRoleLabel(organizationSessionStore.membershipRole)} size='small' sx={{ ml: 1 }} />
				)}
			</Button>

			<Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
				{organizationSessionStore.organizations.map(organization => (
					<MenuItem
						key={organization.id}
						selected={isOrganizationSelected(organization, organizationSessionStore.selectedOrganizationId)}
						onClick={() => chooseOrganization(organization.id)}
					>
						<ListItemText
							primary={organizationLabel(organization, translation)}
							secondary={organization.kind === 'PERSONAL'
								? organization.membership?.displayName || null
								: membershipRoleLabel(organization.membership?.role)}
						/>
					</MenuItem>
				))}
				<Divider />
				{canCreateTeam(Boolean(sessionStore.user)) && (
					<MenuItem onClick={() => { setAnchor(null); setDialog('create') }}>
						<AddBusinessIcon fontSize='small' sx={{ mr: 1 }} /> Создать агентство
					</MenuItem>
				)}
				{organizationManagementStore.canInvite && (
					<MenuItem onClick={() => { setAnchor(null); setRole(roleOptions[0]); setDialog('invite') }}>
						<MailOutlineIcon fontSize='small' sx={{ mr: 1 }} /> Пригласить в агентство
					</MenuItem>
				)}
				<MenuItem onClick={() => { setAnchor(null); setDialog('accept') }}>
					Принять приглашение
				</MenuItem>
			</Menu>

			<Dialog open={dialog === 'create'} onClose={organizationManagementStore.creatingTeam ? undefined : closeDialog} fullWidth maxWidth='sm'>
				<Box component='form' onSubmit={submitTeam}>
					<DialogTitle>Новое агентство</DialogTitle>
					<DialogContent>
						<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
							После создания агентство станет текущим контекстом CRM.
						</Typography>
						{organizationManagementStore.error && <Alert severity='error' sx={{ mb: 2 }}>{organizationManagementStore.error}</Alert>}
						<TextField
							autoFocus
							required
							fullWidth
							label='Название агентства'
							value={teamName}
							onChange={event => setTeamName(event.target.value)}
							disabled={organizationManagementStore.creatingTeam}
							inputProps={{ maxLength: 200 }}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeDialog} disabled={organizationManagementStore.creatingTeam}>Отмена</Button>
						<Button type='submit' variant='contained' disabled={organizationManagementStore.creatingTeam || !teamName.trim()}>
							{organizationManagementStore.creatingTeam ? 'Создание…' : 'Создать'}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>

			<Dialog open={dialog === 'invite'} onClose={organizationManagementStore.creatingInvitation ? undefined : closeDialog} fullWidth maxWidth='sm'>
				<Box component='form' onSubmit={submitInvitation}>
					<DialogTitle>Приглашение в {label}</DialogTitle>
					<DialogContent>
						{organizationManagementStore.error && <Alert severity='error' sx={{ mb: 2 }}>{organizationManagementStore.error}</Alert>}
						<Stack spacing={2} sx={{ mt: 1 }}>
							<TextField
								autoFocus
								required
								type='email'
								label='Электронная почта участника'
								value={email}
								onChange={event => setEmail(event.target.value)}
								disabled={organizationManagementStore.creatingInvitation}
							/>
							<Select
								value={role}
								onChange={event => setRole(event.target.value)}
								disabled={organizationManagementStore.creatingInvitation}
								inputProps={{ 'aria-label': 'Роль приглашения' }}
							>
								{roleOptions.map(option => <MenuItem key={option} value={option}>{membershipRoleLabel(option)}</MenuItem>)}
							</Select>
							{organizationManagementStore.createdInvitationToken && (
								<Alert severity='success'>
									Приглашение создано. Передайте пользователю код: <strong>{organizationManagementStore.createdInvitationToken}</strong>
								</Alert>
							)}
							{organizationManagementStore.pendingInvitations.map(invitation => (
								<Box key={invitation.id} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
									<Typography>{invitation.email}</Typography>
									<Typography variant='caption'>{membershipRoleLabel(invitation.role)} · {invitationStatusLabel(invitation.state)}</Typography>
								</Box>
							))}
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeDialog} disabled={organizationManagementStore.creatingInvitation}>Закрыть</Button>
						<Button type='submit' variant='contained' disabled={organizationManagementStore.creatingInvitation || !email.trim() || !roleOptions.includes(role)}>
							{organizationManagementStore.creatingInvitation ? 'Отправка…' : 'Создать приглашение'}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>

			<Dialog open={dialog === 'accept'} onClose={organizationManagementStore.acceptingInvitation ? undefined : closeDialog} fullWidth maxWidth='sm'>
				<Box component='form' onSubmit={submitAcceptance}>
					<DialogTitle>Принять приглашение</DialogTitle>
					<DialogContent>
						{organizationManagementStore.error && <Alert severity='error' sx={{ mb: 2 }}>{organizationManagementStore.error}</Alert>}
						<TextField
							autoFocus
							required
							fullWidth
							label='Код приглашения'
							value={token}
							onChange={event => setToken(event.target.value)}
							disabled={organizationManagementStore.acceptingInvitation}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={closeDialog} disabled={organizationManagementStore.acceptingInvitation}>Отмена</Button>
						<Button type='submit' variant='contained' disabled={organizationManagementStore.acceptingInvitation || !token.trim()}>
							{organizationManagementStore.acceptingInvitation ? 'Принятие…' : 'Принять'}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>
		</>
	)
})

export default OrganizationSwitcher
